import { Sunrise, NotebookPen, BookOpen, GraduationCap } from "lucide-react";
import { weekLetter } from "./CalendarView";
import { useClock, fmtTime } from "../lib/clockFormat";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function nextSchoolDay(days) {
  const now = new Date();
  for (let i = 1; i <= 7; i++) {
    const nd = new Date(now);
    nd.setDate(now.getDate() + i);
    const wd = nd.toLocaleDateString("en-GB", { weekday: "long" });
    if (days.includes(wd)) return { day: wd, date: nd };
  }
  return null;
}

export default function TomorrowGlance({ schedule, entries, notes, homework = [], exams = [], resolveEntry }) {
  const h24 = useClock();
  const days = schedule?.days || [];
  const target = nextSchoolDay(days);
  if (!target) return null;
  const { day, date } = target;
  const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const weeks = schedule.weeks || 1;
  const prefix = weeks === 2 ? `${weekLetter(ds)}:` : "";
  const slots = (schedule.day_overrides || {})[day]?.slots || schedule.slots || [];
  const rows = slots
    .map((s, i) => ({ slot: s, entry: s.type === "period" ? entries[`${prefix}${day}:${i}`] : null }))
    .filter((r) => r.slot.type === "period" && r.entry);
  const note = (notes || {})[day];
  const hwDue = homework.filter((h) => h.due_date === ds);
  const examsThatDay = exams.filter((e) => e.date === ds);

  return (
    <div className="nb-card p-5 mb-6 no-print" data-testid="tomorrow-glance">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Sunrise className="w-5 h-5" /> Next school day — {day} {date.getDate()} {MONTHS_SHORT[date.getMonth()]}
        </h2>
        {weeks === 2 && <span className="px-2 py-0.5 rounded-lg border-2 border-zinc-900 bg-[#FDE68A] text-xs font-bold" data-testid="tomorrow-week-badge">Week {weekLetter(ds)}</span>}
      </div>
      {rows.length === 0 && !note && hwDue.length === 0 && examsThatDay.length === 0 ? (
        <p className="text-sm text-zinc-500 font-semibold mt-3" data-testid="tomorrow-empty">Nothing scheduled yet — tap a period on the timetable below to add {day}'s classes.</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {rows.map((r) => {
            const { title, sub } = resolveEntry(r.entry);
            return (
              <div key={`${r.slot.label}-${r.slot.start}`} className="flex items-center gap-3 text-sm" data-testid={`tomorrow-row-${r.slot.start.replace(":", "")}`}>
                <span className="text-xs font-bold text-zinc-400 w-24 shrink-0">{fmtTime(r.slot.start, h24)}–{fmtTime(r.slot.end, h24)}</span>
                <span className="font-bold">{title}</span>
                {sub && <span className="text-xs text-zinc-500 font-semibold truncate">{sub}</span>}
              </div>
            );
          })}
          {examsThatDay.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm font-bold text-indigo-900" data-testid={`tomorrow-exam-${e.id}`}>
              <GraduationCap className="w-4 h-4 shrink-0" /> Exam: {e.title}{e.start_time ? ` · ${e.start_time}` : ""}
            </div>
          ))}
          {hwDue.map((h) => (
            <div key={h.id} className="flex items-center gap-2 text-sm font-bold text-zinc-700" data-testid={`tomorrow-hw-${h.id}`}>
              <BookOpen className="w-4 h-4 shrink-0" /> Due: {h.text}
            </div>
          ))}
          {note && (
            <div className="flex items-start gap-2 text-sm mt-1 rounded-lg bg-[#FDE68A]/60 border-2 border-zinc-900 p-2" data-testid="tomorrow-note">
              <NotebookPen className="w-4 h-4 shrink-0 mt-0.5" /> <span>{note}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
